// src/pages/Users/Galleries/components/AlbumLightbox.jsx

import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import ReactDOM from "react-dom";
import createDOMPurify from "dompurify";
import {
  FaChevronLeft,
  FaChevronRight,
  FaDownload,
  FaExternalLinkAlt,
  FaTimes,
} from "react-icons/fa";

import { toAbsolutePhotoUrl } from "../../../../services/albumPhotoServices";

import {
  FALLBACK_ALBUM_IMG,
  stripHtml,
} from "../utils/galleriesUtils";

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

/* -------------------------------------------------------
   DOMPurify et styles enrichis sécurisés
-------------------------------------------------------- */

const purify =
  typeof window !== "undefined" ? createDOMPurify(window) : null;

const isColor = (value) => {
  const text = String(value || "")
    .trim()
    .toLowerCase();

  return (
    text === "transparent" ||
    text === "currentcolor" ||
    text === "inherit" ||
    /^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(text) ||
    /^rgba?\(\s*\d+\s*,\s*\d+\s*,\s*\d+(?:\s*,\s*(0|1|0?\.\d+))?\s*\)$/i.test(
      text,
    ) ||
    /^hsla?\(\s*\d+\s*,\s*\d+%\s*,\s*\d+%(?:\s*,\s*(0|1|0?\.\d+))?\s*\)$/i.test(
      text,
    ) ||
    /^[a-z]+$/i.test(text)
  );
};

const isSafeIndentSize = (value) => {
  const text = String(value || "")
    .trim()
    .toLowerCase();

  if (text === "0") return true;

  const match = text.match(/^(\d+(?:\.\d+)?)(px|rem|em)$/);

  if (!match) return false;

  const amount = Number(match[1]);
  const unit = match[2];

  const maximumByUnit = {
    px: 160,
    rem: 10,
    em: 10,
  };

  return amount >= 0 && amount <= maximumByUnit[unit];
};

const isSafeFontSize = (value) => {
  const text = String(value || "")
    .trim()
    .toLowerCase();

  const match = text.match(/^(\d+(?:\.\d+)?)(px|pt|rem|em|%)$/);

  if (!match) return false;

  const amount = Number(match[1]);
  const unit = match[2];

  const limits = {
    px: { min: 8, max: 64 },
    pt: { min: 6, max: 48 },
    rem: { min: 0.5, max: 4 },
    em: { min: 0.5, max: 4 },
    "%": { min: 50, max: 300 },
  };

  return amount >= limits[unit].min && amount <= limits[unit].max;
};

const isSafeFontFamily = (value) => {
  const text = String(value || "").trim();

  if (!text || text.length > 120) return false;

  if (
    text.includes(";") ||
    text.includes("{") ||
    text.includes("}") ||
    text.includes("\\") ||
    /url\s*\(/i.test(text) ||
    /expression\s*\(/i.test(text) ||
    /var\s*\(/i.test(text)
  ) {
    return false;
  }

  return /^[-"'a-z0-9À-ÿ_,\s]+$/i.test(text);
};

const isSafeLineHeight = (value) => {
  const text = String(value || "")
    .trim()
    .toLowerCase();

  if (/^\d+(?:\.\d+)?$/.test(text)) {
    const amount = Number(text);

    return amount >= 0.8 && amount <= 3;
  }

  const match = text.match(/^(\d+(?:\.\d+)?)(px|rem|em|%)$/);

  if (!match) return false;

  const amount = Number(match[1]);
  const unit = match[2];

  const limits = {
    px: { min: 8, max: 80 },
    rem: { min: 0.8, max: 5 },
    em: { min: 0.8, max: 5 },
    "%": { min: 80, max: 300 },
  };

  return amount >= limits[unit].min && amount <= limits[unit].max;
};

const sanitizeInlineStyle = (styleText = "") => {
  const ALLOWED_STYLES = {
    color: (value) => isColor(value),

    "background-color": (value) => isColor(value),

    "text-align": (value) =>
      ["left", "right", "center", "justify"].includes(
        String(value).trim().toLowerCase(),
      ),

    "font-weight": (value) =>
      /^(normal|bold|bolder|lighter|[1-9]00)$/.test(
        String(value).trim().toLowerCase(),
      ),

    "font-style": (value) =>
      /^(normal|italic|oblique)$/.test(
        String(value).trim().toLowerCase(),
      ),

    "font-size": (value) => isSafeFontSize(value),

    "font-family": (value) => isSafeFontFamily(value),

    "line-height": (value) => isSafeLineHeight(value),

    "text-decoration": (value) =>
      /^(none|underline|line-through|overline)$/.test(
        String(value).trim().toLowerCase(),
      ),

    "margin-left": (value) => isSafeIndentSize(value),

    "padding-left": (value) => isSafeIndentSize(value),

    "text-indent": (value) => isSafeIndentSize(value),

    "list-style-type": (value) =>
      [
        "none",
        "disc",
        "circle",
        "square",
        "decimal",
        "lower-alpha",
        "upper-alpha",
        "lower-roman",
        "upper-roman",
      ].includes(String(value).trim().toLowerCase()),
  };

  return String(styleText)
    .split(";")
    .map((rule) => rule.trim())
    .filter(Boolean)
    .map((rule) => {
      const separatorIndex = rule.indexOf(":");

      if (separatorIndex === -1) return null;

      const property = rule
        .slice(0, separatorIndex)
        .trim()
        .toLowerCase();

      const value = rule.slice(separatorIndex + 1).trim();

      return ALLOWED_STYLES[property]?.(value)
        ? `${property}: ${value}`
        : null;
    })
    .filter(Boolean)
    .join("; ");
};

if (purify) {
  purify.removeAllHooks();

  purify.addHook("uponSanitizeAttribute", (node, data) => {
    if (data.attrName === "style") {
      const cleanStyle = sanitizeInlineStyle(data.attrValue);

      if (cleanStyle) {
        data.attrValue = cleanStyle;
      } else {
        data.keepAttr = false;
      }
    }
  });

  purify.addHook("afterSanitizeAttributes", (node) => {
    if (node.tagName !== "A") return;

    const href = node.getAttribute("href") || "";

    if (/^\s*javascript:/i.test(href)) {
      node.removeAttribute("href");
    }

    node.setAttribute("target", "_blank");
    node.setAttribute("rel", "noopener noreferrer");
  });
}

/* -------------------------------------------------------
   Classes du contenu enrichi sur fond sombre
-------------------------------------------------------- */

const RICH_TEXT_CLASSES = cn(
  "album-lightbox-rich-content",
  "prose prose-invert max-w-none break-words",
  "text-sm leading-6 text-white",
  "prose-p:text-white",

  // Paragraphes
  "prose-p:my-2",
  "[&_p]:whitespace-pre-wrap",

  // Titres
  "prose-headings:my-3",
  "prose-headings:font-barlow",
  "prose-headings:font-bold",
  "prose-headings:!text-white",

  // Texte enrichi
  "prose-strong:!text-white",
  "prose-b:!text-white",
  "prose-a:font-bold",
  "prose-a:text-orange-300",
  "hover:prose-a:underline",

  // Listes à puces
  "[&_ul]:!my-3",
  "[&_ul]:!list-disc",
  "[&_ul]:!pl-6",

  // Listes numérotées
  "[&_ol]:!my-3",
  "[&_ol]:!list-decimal",
  "[&_ol]:!pl-6",

  // Éléments des listes
  "[&_li]:!my-1",
  "[&_li]:!pl-1",
  "[&_li]:whitespace-pre-wrap",

  // Sous-listes
  "[&_ul_ul]:!my-2",
  "[&_ul_ul]:!list-[circle]",
  "[&_ul_ul]:!pl-5",
  "[&_ul_ul_ul]:!list-[square]",

  "[&_ol_ol]:!my-2",
  "[&_ol_ol]:!list-[lower-alpha]",
  "[&_ol_ol]:!pl-5",
  "[&_ol_ol_ol]:!list-[lower-roman]",

  // Alignements Quill
  "[&_.ql-align-center]:!text-center",
  "[&_.ql-align-right]:!text-right",
  "[&_.ql-align-justify]:!text-justify",

  // Indentations Quill
  "[&_.ql-indent-1]:!ml-6",
  "[&_.ql-indent-2]:!ml-12",
  "[&_.ql-indent-3]:!ml-16",
  "[&_.ql-indent-4]:!ml-20",
  "[&_.ql-indent-5]:!ml-24",

  // Tailles Quill
  "[&_.ql-size-small]:!text-xs",
  "[&_.ql-size-large]:!text-xl",
  "[&_.ql-size-huge]:!text-3xl",

  // Polices Quill
  "[&_.ql-font-serif]:!font-serif",
  "[&_.ql-font-monospace]:!font-mono",

  // Citations
  "prose-blockquote:my-3",
  "prose-blockquote:border-orange-300",
  "prose-blockquote:bg-white/10",
  "prose-blockquote:px-4",
  "prose-blockquote:py-2",
  "prose-blockquote:text-white",
  "prose-blockquote:not-italic",

  // Séparateurs et code
  "prose-hr:border-white/20",
  "prose-code:break-words",

  // Médias masqués dans la légende
  "[&_img]:hidden",
  "[&_iframe]:hidden",
  "[&_video]:hidden",

  // Marges externes
  "[&>*:first-child]:mt-0",
  "[&>*:last-child]:mb-0",
);

/* -------------------------------------------------------
   Helpers
-------------------------------------------------------- */

function sanitizeHtml(html = "") {
  if (!purify) return "";

  return purify.sanitize(String(html || ""), {
    USE_PROFILES: {
      html: true,
    },
    ADD_ATTR: [
      "style",
      "target",
      "rel",
      "class",
      "data-list",
    ],
    ALLOW_DATA_ATTR: true,
    FORBID_TAGS: ["style"],
  });
}

function onImgError(event) {
  const image = event?.currentTarget;

  if (!image) return;

  if (image.dataset.fallbackApplied === "true") return;

  image.dataset.fallbackApplied = "true";
  image.src = FALLBACK_ALBUM_IMG;
}

/* -------------------------------------------------------
   Composant
-------------------------------------------------------- */

export default function AlbumLightbox({
  isOpen,
  photos = [],
  startIndex = 0,
  onClose,
}) {
  const [index, setIndex] = useState(startIndex);

  useEffect(() => {
    if (!isOpen) return;

    const safeIndex = Math.min(
      Math.max(Number(startIndex) || 0, 0),
      Math.max(photos.length - 1, 0),
    );

    setIndex(safeIndex);
  }, [isOpen, photos.length, startIndex]);

  const current = photos[index] || {};

  const currentSrc = useMemo(() => {
    return (
      toAbsolutePhotoUrl(current?.photo_url || "") ||
      FALLBACK_ALBUM_IMG
    );
  }, [current?.photo_url]);

  const captionText = useMemo(() => {
    return stripHtml(current?.description || "");
  }, [current?.description]);

  const captionHtml = useMemo(() => {
    if (!captionText) return "";

    return sanitizeHtml(current?.description || "");
  }, [captionText, current?.description]);

  const previous = useCallback(() => {
    if (photos.length <= 1) return;

    setIndex(
      (previousIndex) =>
        (previousIndex - 1 + photos.length) %
        photos.length,
    );
  }, [photos.length]);

  const next = useCallback(() => {
    if (photos.length <= 1) return;

    setIndex(
      (previousIndex) =>
        (previousIndex + 1) % photos.length,
    );
  }, [photos.length]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = "hidden";

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose?.();
      }

      if (
        event.key === "ArrowLeft" &&
        photos.length > 1
      ) {
        previous();
      }

      if (
        event.key === "ArrowRight" &&
        photos.length > 1
      ) {
        next();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener(
        "keydown",
        handleKeyDown,
      );
    };
  }, [
    isOpen,
    next,
    onClose,
    photos.length,
    previous,
  ]);

  /*
   * Préchargement de la photo précédente et suivante.
   */
  useEffect(() => {
    if (!isOpen || photos.length <= 1) return;

    const preload = (targetIndex) => {
      const photo = photos[targetIndex];

      if (!photo?.photo_url) return;

      const image = new Image();

      image.src = toAbsolutePhotoUrl(
        photo.photo_url,
      );
    };

    preload((index + 1) % photos.length);

    preload(
      (index - 1 + photos.length) %
        photos.length,
    );
  }, [index, isOpen, photos]);

  if (!isOpen || photos.length === 0) {
    return null;
  }

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[9999] bg-slate-950/90 p-3 backdrop-blur-sm sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-label="Visionneuse photo"
      onClick={onClose}
    >
      <div
        className="flex h-full flex-col"
        onClick={(event) => event.stopPropagation()}
      >
        {/* En-tête */}

        <header className="flex shrink-0 items-center justify-between gap-3 text-white">
          <div className="rounded-full bg-white/10 px-4 py-2 text-sm font-bold backdrop-blur">
            {index + 1} / {photos.length}
          </div>

          <button
            type="button"
            onClick={onClose}
            className="flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur transition hover:bg-white/20 focus:outline-none focus-visible:ring-4 focus-visible:ring-white/30 active:scale-95"
            aria-label="Fermer"
          >
            <FaTimes />
          </button>
        </header>

        {/* Photo */}

        <div className="relative flex min-h-0 flex-1 items-center justify-center py-4">
          {photos.length > 1 ? (
            <button
              type="button"
              onClick={previous}
              className="absolute left-0 z-10 flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur transition hover:bg-white/20 focus:outline-none focus-visible:ring-4 focus-visible:ring-white/30 active:scale-95 sm:left-4"
              aria-label="Photo précédente"
            >
              <FaChevronLeft />
            </button>
          ) : null}

          <div className="flex max-h-full w-full max-w-6xl flex-col items-center">
            <img
              src={currentSrc}
              alt={
                captionText ||
                `Photo ${index + 1}`
              }
              className={cn(
                "mx-auto w-full rounded-[1.5rem] object-contain shadow-2xl",
                captionHtml
                  ? "max-h-[62dvh]"
                  : "max-h-[72dvh]",
              )}
              onError={onImgError}
            />

            {/* Légende enrichie */}

            {captionHtml ? (
              <div className="mx-auto mt-3 max-h-[160px] w-full max-w-3xl overflow-y-auto overflow-x-hidden rounded-2xl border border-white/10 bg-white/10 px-4 py-3 text-white backdrop-blur">
                <div
                  className={RICH_TEXT_CLASSES}
                  style={{ tabSize: 4 }}
                  dangerouslySetInnerHTML={{
                    __html: captionHtml,
                  }}
                />
              </div>
            ) : null}
          </div>

          {photos.length > 1 ? (
            <button
              type="button"
              onClick={next}
              className="absolute right-0 z-10 flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur transition hover:bg-white/20 focus:outline-none focus-visible:ring-4 focus-visible:ring-white/30 active:scale-95 sm:right-4"
              aria-label="Photo suivante"
            >
              <FaChevronRight />
            </button>
          ) : null}
        </div>

        {/* Actions */}

        <footer className="flex shrink-0 justify-end gap-2">
          <a
            href={currentSrc}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex h-12 items-center justify-center gap-2 rounded-2xl bg-white/10 px-4 text-sm font-bold text-white backdrop-blur transition hover:bg-white/20 focus:outline-none focus-visible:ring-4 focus-visible:ring-white/30 active:scale-95"
          >
            <FaExternalLinkAlt />

            <span className="hidden sm:inline">
              Original
            </span>
          </a>

          <a
            href={currentSrc}
            download
            className="inline-flex h-12 items-center justify-center gap-2 rounded-2xl bg-white px-4 text-sm font-bold text-slate-950 transition hover:bg-slate-100 focus:outline-none focus-visible:ring-4 focus-visible:ring-white/30 active:scale-95"
          >
            <FaDownload />

            <span className="hidden sm:inline">
              Télécharger
            </span>
          </a>
        </footer>
      </div>
    </div>,
    document.body,
  );
}